import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from './features/login/login.component';
import { OtpComponent } from './features/login/otp.component';
import { ForgotPasswordComponent } from './features/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './features/reset-password/reset-password.component';
import { NewRequestComponent } from './features/admin/new-request/new-request.component';
import { HistoryComponent } from './features/admin/history/history.component';
import { AddressBookComponent } from './features/address-book/address-book.component';
import { DataViewerComponent } from './features/data-viewer/data-viewer.component';
import { UsersComponent } from './features/admin/users/users.component';
import { UserAuditLogComponent } from './features/admin/users/user-audit-log.component';
import { CampaignsComponent } from './features/admin/campaigns/campaigns.component';
import { FeatureFlagsComponent } from './features/admin/feature-flags/feature-flags.component';
import { StreetSearchComponent } from './features/admin/street-search/street-search.component';
import { StreetInfoComponent } from './features/admin/street-search/street-info.component';
import { MultiSearchComponent } from './features/admin/multi-search/multi-search.component';
import { MyAccountComponent } from './features/my-account/my-account.component';
import { BillingPlanSelectorComponent } from './features/billing-plan-selector/billing-plan-selector.component';
import { RegisterComponent } from './features/register/register.component';
import { AuthGuard } from './core/auth/auth.guard';
import { AdminGuard } from './core/auth/admin.guard';
import { PlanSelectionGuard } from './core/auth/plan-selection.guard';
import { FeatureGuard } from './core/auth/feature.guard';
import { TwoFactorGuard } from './core/auth/two-factor.guard';
import { PlatformAdminGuard } from './core/auth/platform-admin.guard';
import { TrustRequestComponent } from './features/admin/trust-request/trust-request.component';
import { TrustDataViewerComponent } from './features/trust-data-viewer/trust-data-viewer.component';
import { DigitalOnboardingListComponent } from './features/digital-onboarding/digital-onboarding-list.component';
import { DigitalOnboardingWizardComponent } from './features/digital-onboarding/digital-onboarding-wizard.component';
import { DigitalOnboardingResultsComponent } from './features/digital-onboarding/digital-onboarding-results.component';
import { MultiSearchPendingGuard } from './core/auth/multi-search-pending.guard';
import { PlatformAdminComponent } from './features/platform-admin/platform-admin.component';
import { PlatformStatsComponent } from './features/platform-admin/platform-stats.component';
import { PlatformOrganizationBillingComponent } from './features/platform-admin/platform-organization-billing.component';

const routes: Routes = [
  { path: 'login', component: LoginComponent },
  { path: 'forgot-password', component: ForgotPasswordComponent },
  { path: 'register', component: RegisterComponent },
  { path: 'otp', component: OtpComponent, canActivate: [AuthGuard] },
  { path: 'reset-password', component: ResetPasswordComponent },
  { path: 'platform-admin', component: PlatformAdminComponent, canActivate: [AuthGuard, PlatformAdminGuard] },
  { path: 'platform-admin/stats', component: PlatformStatsComponent, canActivate: [AuthGuard, PlatformAdminGuard] },
  {
    path: 'platform-admin/feature-flags',
    component: FeatureFlagsComponent,
    canActivate: [AuthGuard, PlatformAdminGuard],
    data: { mode: 'platform' },
  },
  {
    path: 'platform-admin/organizations/:orgId/billing',
    component: PlatformOrganizationBillingComponent,
    canActivate: [AuthGuard, PlatformAdminGuard],
  },
  { path: 'billing/plan-selector', component: BillingPlanSelectorComponent, canActivate: [AuthGuard, TwoFactorGuard] },
  { path: 'my-account', component: MyAccountComponent, canActivate: [AuthGuard, TwoFactorGuard, PlanSelectionGuard] },
    {
      path: 'admin',
      canActivate: [AuthGuard, TwoFactorGuard, PlanSelectionGuard],
      children: [
        { path: 'new-request', component: NewRequestComponent, canActivate: [AdminGuard] },
        { path: 'trust-request', component: TrustRequestComponent, canActivate: [FeatureGuard], data: { featureKey: 'trust_search' } },
        {
          path: 'users/:userId/audit-log',
          component: UserAuditLogComponent,
          canActivate: [AdminGuard]
        },
        { path: 'users', component: UsersComponent, canActivate: [AdminGuard] },
        {
          path: 'campaigns',
          component: CampaignsComponent,
          canActivate: [AdminGuard, FeatureGuard],
          data: { featureKey: 'campaigns' },
        },
        { path: 'history', component: HistoryComponent },
        { path: 'multi-search/:requestBatchId', component: MultiSearchComponent, canDeactivate: [MultiSearchPendingGuard] },
        { path: 'address-book', component: AddressBookComponent },
        {
          path: 'street-search',
          component: StreetSearchComponent,
          canActivate: [FeatureGuard],
          data: { featureKey: 'street_search' }
        },
        {
          path: 'street-search/info',
          component: StreetInfoComponent,
          canActivate: [FeatureGuard],
          data: { featureKey: 'street_search' }
        },
        { path: 'data/:requestId', component: DataViewerComponent },
        { path: 'trust-data/:requestId', component: TrustDataViewerComponent },
        {
          path: 'digital-onboarding',
          component: DigitalOnboardingListComponent,
          canActivate: [FeatureGuard],
          data: { featureKey: 'digital_onboarding' },
        },
        {
          path: 'digital-onboarding/new',
          component: DigitalOnboardingWizardComponent,
          canActivate: [FeatureGuard],
          data: { featureKey: 'digital_onboarding' },
        },
        {
          path: 'digital-onboarding/:requestId',
          component: DigitalOnboardingResultsComponent,
          canActivate: [FeatureGuard],
          data: { featureKey: 'digital_onboarding' },
        },
      ]
    },
  {
    path: 'user',
    canActivate: [AuthGuard, TwoFactorGuard, PlanSelectionGuard],
    children: [
      { path: 'data-request', component: NewRequestComponent },
      { path: 'trust-request', component: TrustRequestComponent, canActivate: [FeatureGuard], data: { featureKey: 'trust_search' } },
      { path: 'address-book', component: AddressBookComponent },
      { path: 'multi-search/:requestBatchId', component: MultiSearchComponent, canDeactivate: [MultiSearchPendingGuard] },
      { path: 'trust-data/:requestId', component: TrustDataViewerComponent },
      {
        path: 'digital-onboarding',
        component: DigitalOnboardingListComponent,
        canActivate: [FeatureGuard],
        data: { featureKey: 'digital_onboarding' },
      },
      {
        path: 'digital-onboarding/new',
        component: DigitalOnboardingWizardComponent,
        canActivate: [FeatureGuard],
        data: { featureKey: 'digital_onboarding' },
      },
      {
        path: 'digital-onboarding/:requestId',
        component: DigitalOnboardingResultsComponent,
        canActivate: [FeatureGuard],
        data: { featureKey: 'digital_onboarding' },
      },
    ]
  },
  { path: '', redirectTo: '/login', pathMatch: 'full' },
  { path: '**', redirectTo: '/login' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }

